// Detalle de pago en overlay responsive (Modal desktop / Drawer móvil).
// Muestra todos los datos del PagoClienteResponse SIN navegar; reemplaza a
// PagoDetailPage en el flujo de listado (fuente de verdad de campos y
// acciones).
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Space, Tooltip, Typography, theme } from 'antd';
import { DetalleOverlay } from '../../../components/DetalleOverlay';
import { CampoInfo, CamposInfoGrid, MontoDestacado } from '../../../components/CampoInfo';
import { EmptyState } from '../../../components/EmptyState';
import { PagoStatusTag } from './PagoStatusTag';
import { AnularPagoModal } from './AnularPagoModal';
import { DownloadReceiptButton } from './DownloadReceiptButton';
import { ReceiptVersionsButton } from './ReceiptVersions';
import { usePago } from '../api/usePagos';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { DATE_FORMAT, parseDateOnly } from '../../../lib/date';
import { coordenadasValidas } from '../../../lib/geo';
import { VerMapaButton } from '../../../components/VerMapaButton';
import dayjs from 'dayjs';
import type { PagoClienteResponse } from '@aid/shared';
import { formatCurrency, Permission } from '@aid/shared';

const { Text } = Typography;

interface PagoDetailModalProps {
  id: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

function SeccionHeader({ children }: { children: string }) {
  const { token } = theme.useToken();
  return (
    <Text
      type="secondary"
      style={{
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        marginBottom: 12,
        color: token.colorTextSecondary,
      }}
    >
      {children}
    </Text>
  );
}

function Seccion({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return (
    <section
      style={{
        marginTop: 20,
        paddingTop: 16,
        borderTop: `1px solid ${token.colorSplit}`,
      }}
    >
      {children}
    </section>
  );
}

function ClienteIdentidad({ pago, status }: { pago: PagoClienteResponse; status: ReactNode }) {
  const t = useT();
  const { token } = theme.useToken();
  const clienteId = pago.cliente?.id ?? pago.idCliente;
  const nombre = pago.cliente?.nombre ?? pago.nombreCliente ?? `#${pago.idCliente}`;
  const codigo =
    pago.cliente?.ip ?? pago.ipCliente ?? (pago.cliente ? `#${pago.cliente.id}` : null);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text
          style={{
            display: 'block',
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.3,
            color: token.colorText,
            overflowWrap: 'break-word',
          }}
        >
          {nombre}
        </Text>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 4,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
            {codigo ?? '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
            ·
          </Text>
          <Link
            to={`/clientes/${clienteId}`}
            style={{ fontSize: 12, lineHeight: 1.4, color: token.colorPrimary }}
          >
            {t('components.verPerfil')}
          </Link>
        </div>
      </div>
      <div style={{ flexShrink: 0, paddingTop: 2 }}>{status}</div>
    </div>
  );
}

function formatDate(value: Date | string | null | undefined): string | null {
  // Date-only contract: legacy ISO-midnight values recover the calendar date.
  return parseDateOnly(value)?.format(DATE_FORMAT) ?? null;
}

function parseMoney(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function PagoDetailModal({ id, onClose, onSuccess }: PagoDetailModalProps) {
  const t = useT();
  const { data: pago, isLoading, error, refetch } = usePago(id ?? 0);
  const { data: tiposPago } = useCatalogos('TIPO_PAGO');
  const canAnular = useHasPermission(Permission.PAGOS_ANULAR);
  const { token } = theme.useToken();

  const [anularModalOpen, setAnularModalOpen] = useState(false);

  const ubicacion = coordenadasValidas(pago?.latitud, pago?.longitud);
  const periodoCubierto =
    (pago?.aplicacionesCount ?? 0) > 0 && pago?.periodoCubiertoInicio && pago?.periodoCubiertoFin
      ? `${formatDate(pago.periodoCubiertoInicio)} – ${formatDate(pago.periodoCubiertoFin)}`
      : null;

  const tipoPagoLabel = pago
    ? ((tiposPago ?? []).find((c) => Number(c.id) === Number(pago.idTipoPago))?.value ??
      `#${pago.idTipoPago}`)
    : '';

  const creditoGenerado = pago
    ? pago.aplicacion?.creditoGenerado !== undefined
      ? pago.aplicacion.creditoGenerado
      : pago.montoCredito
    : undefined;
  const tieneCreditoDato = creditoGenerado !== undefined && creditoGenerado !== null;
  const montoAplicado = pago
    ? (pago.aplicacion?.montoAplicadoTotal ??
      (tieneCreditoDato
        ? Math.max(0, Number(pago.valor) - (parseMoney(creditoGenerado) ?? 0)).toFixed(2)
        : null))
    : null;
  const tieneAplicacion = Boolean(
    pago &&
    (pago.aplicacion !== undefined || tieneCreditoDato || (pago.aplicacionesCount ?? 0) > 0),
  );

  const footer = (
    <Space wrap>
      {canAnular && pago && !pago.motivoAnulacion && (
        <Button danger onClick={() => setAnularModalOpen(true)}>
          {t('pages.pagos.anular')}
        </Button>
      )}
      <Button onClick={onClose}>{t('common.close')}</Button>
      {pago && !pago.motivoAnulacion && <DownloadReceiptButton pago={pago} />}
      {pago?.motivoAnulacion ? <ReceiptVersionsButton idPago={pago.id} /> : null}
    </Space>
  );

  return (
    <>
      <DetalleOverlay
        open={id !== null}
        onClose={onClose}
        title={id !== null ? t('pages.pagos.detailId', { id }) : undefined}
        footer={footer}
        loading={isLoading}
      >
        {error || !pago ? (
          <EmptyState
            title={t('pages.pagos.notFound')}
            description={t('pages.pagos.notFoundDetail')}
          />
        ) : (
          <div>
            <ClienteIdentidad
              pago={pago}
              status={
                <PagoStatusTag
                  motivoAnulacion={pago.motivoAnulacion}
                  idEstadoPago={pago.idEstadoPago}
                />
              }
            />

            <Seccion>
              <SeccionHeader>{t('pages.pagos.informacionPago')}</SeccionHeader>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 16,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 4 }}
                  >
                    {t('pages.pagos.valor')}
                  </Text>
                  <div
                    style={{
                      background: token.colorFillTertiary,
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: token.borderRadiusLG,
                      padding: '10px 14px',
                    }}
                  >
                    <MontoDestacado
                      value={formatCurrency(pago.valor)}
                      color={Number(pago.valor) > 0 ? token.colorSuccess : undefined}
                    />
                  </div>
                </div>
                <CamposInfoGrid>
                  <CampoInfo label={t('pages.pagos.tipoPago')} value={tipoPagoLabel} />
                  <CampoInfo
                    label={t('pages.pagos.fechaPago')}
                    value={formatDate(pago.fechaPago)}
                  />
                </CamposInfoGrid>
              </div>
              <CamposInfoGrid>
                <CampoInfo
                  label={t('pages.pagos.modalidad')}
                  value={
                    pago.modalidad === 'ADELANTADO'
                      ? t('pages.pagos.modalidadAdelantado')
                      : pago.modalidad === 'NORMAL'
                        ? t('pages.pagos.modalidadNormal')
                        : t('pages.clientes.actividad.historica')
                  }
                />
                {pago.modalidad === 'ADELANTADO' && pago.mesesAdelantado ? (
                  <CampoInfo
                    label={t('pages.pagos.mesesAdelantado')}
                    value={pago.mesesAdelantado}
                  />
                ) : null}
              </CamposInfoGrid>
            </Seccion>

            {tieneAplicacion ? (
              <Seccion>
                <SeccionHeader>{t('pages.pagos.aplicacion')}</SeccionHeader>
                <CamposInfoGrid>
                  {montoAplicado !== null ? (
                    <CampoInfo
                      label={t('pages.pagos.montoAplicado')}
                      value={formatCurrency(montoAplicado)}
                    />
                  ) : null}
                  {tieneCreditoDato ? (
                    <CampoInfo
                      label={t('pages.pagos.creditoGenerado')}
                      value={
                        <span style={{ color: token.colorSuccess, fontWeight: 600 }}>
                          {formatCurrency(creditoGenerado ?? '0')}
                        </span>
                      }
                    />
                  ) : null}
                  {(pago.aplicacionesCount ?? 0) > 0 ? (
                    <CampoInfo
                      label={t('pages.pagos.facturasAplicadas')}
                      value={String(pago.aplicacionesCount)}
                    />
                  ) : null}
                  {pago.saldoInicial !== null && pago.saldoInicial !== undefined ? (
                    <CampoInfo
                      label={t('pages.clientes.modal.saldoAntesPago')}
                      value={formatCurrency(pago.saldoInicial)}
                    />
                  ) : null}
                </CamposInfoGrid>
                {pago.aplicacion?.aplicaciones && pago.aplicacion.aplicaciones.length > 0 ? (
                  <div style={{ marginTop: 16 }}>
                    <Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 8 }}
                    >
                      {t('pages.pagos.aplicacionesDetalle')}
                    </Text>
                    <div style={{ borderTop: `1px solid ${token.colorSplit}` }}>
                      {pago.aplicacion.aplicaciones.map((aplicacion) => (
                        <div
                          key={aplicacion.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: '10px 0',
                            borderBottom: `1px solid ${token.colorSplit}`,
                          }}
                        >
                          <Text style={{ fontWeight: 500 }}>
                            {t('pages.pagos.aplicacionFactura', { id: aplicacion.idFactura })}
                          </Text>
                          <Text style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                            {formatCurrency(aplicacion.montoAplicado)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Seccion>
            ) : null}

            {pago.fechaInicio || pago.fechaFin || periodoCubierto ? (
              <Seccion>
                <SeccionHeader>{t('pages.pagos.periodo')}</SeccionHeader>
                <CamposInfoGrid>
                  <CampoInfo
                    label={t('pages.clientes.modal.periodo')}
                    value={[formatDate(pago.fechaInicio), formatDate(pago.fechaFin)]
                      .filter(Boolean)
                      .join(' – ')}
                  />
                  {periodoCubierto ? (
                    <CampoInfo
                      label={t('pages.clientes.modal.periodoCubierto')}
                      value={periodoCubierto}
                    />
                  ) : null}
                </CamposInfoGrid>
              </Seccion>
            ) : null}

            {ubicacion ? (
              <Seccion>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <SeccionHeader>{t('pages.pagos.ubicacion')}</SeccionHeader>
                  <VerMapaButton latitud={ubicacion.lat} longitud={ubicacion.lng} />
                </div>
                <Tooltip title={t('pages.pagos.coordsAuto')}>
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 12, lineHeight: 1.5 }}
                  >
                    {t('pages.pagos.coordsAuto')}
                  </Text>
                </Tooltip>
              </Seccion>
            ) : null}

            {pago.comentarios ? (
              <Seccion>
                <SeccionHeader>{t('pages.pagos.comentarios')}</SeccionHeader>
                <Text style={{ display: 'block', lineHeight: 1.5, overflowWrap: 'break-word' }}>
                  {pago.comentarios}
                </Text>
              </Seccion>
            ) : null}

            <Seccion>
              <SeccionHeader>{t('pages.clientes.infoAuditoria')}</SeccionHeader>
              <CamposInfoGrid>
                <CampoInfo label={t('common.createdAt')} value={formatDate(pago.fechaCreacion)} />
                <CampoInfo
                  label={t('common.updatedAt')}
                  value={formatDate(pago.fechaModificacion)}
                />
                {pago.usuarioModifica ? (
                  <CampoInfo
                    full
                    label={t('pages.clientes.modificadoPor')}
                    value={pago.usuarioModifica}
                  />
                ) : null}
              </CamposInfoGrid>
            </Seccion>

            {pago.motivoAnulacion ? (
              <Seccion>
                <SeccionHeader>{t('pages.clientes.modal.anulacion')}</SeccionHeader>
                <CamposInfoGrid>
                  <CampoInfo
                    full
                    label={t('pages.pagos.motivoAnulacion')}
                    value={pago.motivoAnulacion}
                  />
                  <CampoInfo
                    full
                    label={t('pages.pagos.fechaAnulacion')}
                    value={
                      pago.fechaAnulacion
                        ? dayjs(pago.fechaAnulacion).format(`${DATE_FORMAT} HH:mm`)
                        : undefined
                    }
                  />
                </CamposInfoGrid>
              </Seccion>
            ) : null}
          </div>
        )}
      </DetalleOverlay>
      <AnularPagoModal
        open={anularModalOpen}
        pagoId={pago?.id ?? 0}
        onClose={() => setAnularModalOpen(false)}
        onSuccess={() => {
          void refetch();
          onSuccess?.();
        }}
      />
    </>
  );
}
